import type { ReactNode } from 'react';
import React from 'react';

import { FlagsProvider } from '@atlaskit/flag/flags-provider';

import { ProfileCardWrapper } from '@confluence/profile/entry-points/ProfileCardWrapper.ts';

import { toUsableIdentityAccountId } from './toUsableIdentityAccountId.ts';

export type ProfileIdentityProps = {
	userId?: string | null;
	fullName?: string;
	children: ReactNode;
	agent?: Record<string, never>;
	agentJobTitle?: string | null;
	/** Person the agent acted on behalf of, surfaced as an attribution row on the agent card. */
	withUser?: {
		name: string;
		accountId?: string;
		avatarUrl?: string;
	};
	trigger?: 'click' | 'hover';
	position?: React.ComponentProps<typeof ProfileCardWrapper>['position'];
	offset?: [number, number];
};

export const ProfileIdentity = ({
	userId,
	fullName,
	children,
	agent,
	agentJobTitle,
	withUser,
	trigger = 'click',
	position = 'bottom-start',
	offset = [0, 8],
}: ProfileIdentityProps): React.JSX.Element => {
	const usableUserId = toUsableIdentityAccountId(userId);
	const usableInvokingUserId = toUsableIdentityAccountId(withUser?.accountId);
	const agentInvokedBy = withUser && {
		name: withUser.name,
		avatarUrl: withUser.avatarUrl,
		...(usableInvokingUserId ? { accountId: usableInvokingUserId } : {}),
	};
	if (!usableUserId) {
		return <>{children}</>;
	}
	const card = (
		<ProfileCardWrapper
			userId={usableUserId}
			fullName={fullName}
			trigger={trigger}
			position={position}
			offset={offset}
			{...(agent
				? {
						agent,
						agentJobTitle,
						agentInvokedBy,
					}
				: {})}
		>
			{children}
		</ProfileCardWrapper>
	);

	if (agent) {
		return <FlagsProvider>{card}</FlagsProvider>;
	}

	return card;
};
